export type Locale = "en" | "es";

export const dictionaries = {
  en: {
    "nav.maintenance": "Maintenance",
    "maintenance.subtitle": "Check service status, background processing and workspace data.",
    "maintenance.statusTitle": "Service and data",
    "maintenance.refresh": "Refresh status",
    "maintenance.connected": "Service and storage available",
    "maintenance.checkFailed": "Status check failed",
    "maintenance.lastCheck": "Last checked:",
    "maintenance.temporaryData": "Temporary development data",
    "maintenance.persistentData": "Persistent storage",
    "maintenance.queueTitle": "Event processing",
    "maintenance.pending": "Pending",
    "maintenance.processing": "Processing",
    "maintenance.failed": "Failed",
    "maintenance.queueHint":
      "Recorded events awaiting processing, in progress or stopped after failures. Use the latest check time when reviewing these counts.",
    "maintenance.oldestPending": "Oldest pending event:",
    "maintenance.statusError": "Could not check service and data status. Refresh to try again.",
    "maintenance.dataTitle": "Data cleanup",
    "maintenance.protection":
      "Real accounts, edited records and demo records linked to real work are kept. Workspace, workflow statuses and integration settings are kept too.",
    "maintenance.preview": "Generate preview",
    "maintenance.demo": "Demonstration data",
    "maintenance.start":
      "Generate a read-only preview to see every proposed deletion and the records that will be kept. Nothing is deleted at this step.",
    "maintenance.review": "Review records",
    "maintenance.expires": "Preview expires at",
    "maintenance.action": "Show records",
    "maintenance.delete": "Delete demo records",
    "maintenance.keep": "Keep records",
    "maintenance.search": "Search by name or ID",
    "maintenance.noRecords": "No records match this filter.",
    "maintenance.record": "Record",
    "maintenance.reason": "Reason",
    "maintenance.reason.unchanged_demo": "Identical to the original demo fixture",
    "maintenance.reason.modified_demo": "Demo record has been modified",
    "maintenance.reason.real_data": "Outside the demo fixture",
    "maintenance.reason.current_admin": "Current administrator",
    "maintenance.reason.referenced": "Referenced by a retained record",
    "maintenance.reason.mixed_issue": "Issue contains retained work or history",
    "maintenance.confirmTitle": "Confirm reviewed cleanup",
    "maintenance.irreversible":
      "Deletion is permanent. This page does not create a backup. If data changes, a new preview is required.",
    "maintenance.backup": "I have verified a recent backup and reviewed every proposed deletion.",
    "maintenance.typeConfirmation": "Type DELETE DEMO to confirm",
    "maintenance.loadError": "Could not generate the preview. Try again.",
    "maintenance.saveError":
      "Could not confirm cleanup. Generate a new preview to check the current state before retrying.",
    "maintenance.stale": "The preview expired or data changed. Generate and review a new preview.",
    "maintenance.done": "Cleanup completed. Records deleted:",
    "maintenance.nothingToDelete":
      "There are no unchanged demo records that can be safely deleted.",
    "maintenance.labels": "Labels",
    "maintenance.issueLabels": "Issue labels",
    "maintenance.comments": "Comments",
    "maintenance.activity": "Activity history",
    "maintenance.views": "Saved views",
    "maintenance.runs": "Agent runs",
    "maintenance.artifacts": "Artifacts",
    "maintenance.repositories": "Linked repositories",
    "maintenance.pullRequests": "Pull request links",
    "maintenance.other": "Other records",
    "app.name": "Qualtio WorkHub",
    "app.workspace": "Qualtio",
    "app.loading": "Loading",
    "auth.demoHint": "Use demo@qualt.io in development mode.",
    "auth.email": "Email",
    "auth.error": "Unable to sign in with those credentials.",
    "auth.googleHint": "Only Qualtio Google accounts can sign in.",
    "auth.googleLogin": "Continue with Google",
    "auth.loading": "Loading sign-in options...",
    "auth.login": "Sign in",
    "auth.logout": "Sign out",
    "auth.password": "Password",
    "auth.subtitle": "Agent-first work management for Qualtio.",
    "auth.title": "Sign in to WorkHub",
    "command.open": "Open command palette",
    "command.createIssue": "Create issue",
    "command.placeholder": "Search routes, actions, and issues",
    "command.shortcut": "Ctrl/Cmd+K",
    "command.search": "Search",
    "command.title": "Command palette",
    "command.empty": "No commands or issues match your search.",
    "command.results.one": "1 result",
    "command.results.many": "results",
    "common.language": "Language",
    "common.cancel": "Cancel",
    "common.close": "Close",
    "common.create": "Create",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.remove": "Remove",
    "common.save": "Save",
    "common.saving": "Saving",
    "common.retry": "Try again",
    "common.theme": "Theme",
    "common.themeDark": "Dark",
    "common.themeLight": "Light",
    "settings.adminOnly": "Only admins can change integration settings.",
    "settings.forbidden": "You do not have permission to change these settings.",
    "nav.agents": "Agents",
    "nav.analytics": "Analytics",
    "nav.board": "Board",
    "nav.chapters": "Chapters",
    "nav.clients": "Clients",
    "nav.cycles": "Cycles",
    "nav.github": "GitHub",
    "nav.apiKeys": "API Keys",
    "nav.group.work": "Work",
    "nav.group.agents": "Agents",
    "nav.group.context": "Context",
    "nav.group.planning": "Planning",
    "nav.group.settings": "Settings",
    "nav.myWork": "My Work",
    "nav.inbox": "Inbox",
    "nav.issues": "Issues",
    "nav.products": "Products",
    "nav.projects": "Projects",
    "nav.research": "Research",
    "nav.roadmap": "Roadmap",
    "nav.slack": "Slack",
    "nav.triage": "Triage",
    "nav.users": "Users",
    "search.global": "Search or jump",
    "shell.account": "Account",
    "shell.navigation": "Navigation",
    "shell.skipToContent": "Skip to main content",
    "work.meta": "Core workflow",
    "work.loadError": "Unable to load this workspace view.",
    "work.loadError.desc": "Check your connection and try again. Your filters will be kept.",
    "work.empty": "No issues match this view.",
    "work.empty.desc": "Create an issue or adjust filters to populate this view.",
    "work.home.agentQueueEmpty": "No agents are assigned to your work yet.",
    "work.home.title": "Home / My Work",
    "work.home.subtitle": "Prioritized work by SLA, ownership, and agent confidence.",
    "work.home.priority": "Priority queue",
    "work.home.agentQueue": "Agent queue",
    "work.inbox.title": "Inbox",
    "work.inbox.subtitle": "Unified notifications from work, agents, and integrations.",
    "work.triage.title": "Triage",
    "work.triage.subtitle": "Keyboard-first decision queue for incoming work.",
    "work.issues.title": "Issue List",
    "work.issues.subtitle": "Filter, inspect, and open work without losing context.",
    "work.board.title": "Board",
    "work.board.subtitle": "Operational status board for current work.",
    "work.board.scope": "Board",
    "work.board.allBoards": "All work",
    "work.board.chapterBoard": "Chapter",
    "work.board.initiativeBoard": "Initiative",
    "work.board.selectedBoard": "Selected board",
    "work.metric.open": "Open",
    "work.metric.triage": "Triage",
    "work.metric.agent": "Agent assigned",
    "work.metric.mine": "Mine",
    "work.metric.urgent": "Urgent",
    "work.metric.slaRisk": "SLA risk",
    "work.metric.agentDrafts": "Agent drafts",
    "work.metric.blocked": "Blocked",
    "issue.open": "Open issue",
    "issue.unassigned": "Unassigned",
    "issue.noDescription": "No description yet.",
    "issue.comments": "Comments",
    "issue.timeline": "Activity",
    "issue.agentPanel": "Agent execution",
    "issue.githubPanel": "GitHub",
    "issue.githubPlaceholder": "No pull requests linked.",
    "issue.slackPanel": "Slack context",
    "issue.slackPlaceholder":
      "Thread links will appear here after a Slack message is converted or linked.",
    "issue.slackSkeleton": "Slack skeleton ready",
    "issue.slackNoThread": "No thread linked",
    "issue.artifacts": "Artifacts",
    "issue.artifacts.empty": "No artifacts have been attached yet.",
    "issue.detail.overview": "Overview",
    "issue.detail.events": "events",
    "issue.detail.runs": "runs",
    "issue.detail.noSla": "No SLA",
    "issue.detail.impact": "Impact",
    "issue.detail.testsRunning": "tests running",
    "issue.detail.currentState": "Current state",
    "issue.detail.action.start": "Start work",
    "issue.detail.commentsMeta": "Slack + internal",
    "issue.detail.commentRole": "Internal",
    "issue.detail.noComments": "No comments yet.",
    "issue.detail.liveActivity": "live activity",
    "issue.detail.noActivity": "No activity yet",
    "issue.detail.latestRun": "latest run",
    "issue.detail.runningTests": "Run progress",
    "issue.detail.metadata": "Metadata",
    "issue.detail.fields": "fields",
    "issue.detail.repo": "repo",
    "issue.detail.notFound": "Issue not found.",
    "issue.field.issue": "Issue",
    "issue.field.title": "Title",
    "issue.field.description": "Description",
    "issue.field.status": "Status",
    "issue.field.priority": "Priority",
    "issue.field.type": "Type",
    "issue.field.assigneeUser": "Owner",
    "issue.field.agent": "Agent",
    "issue.field.chapter": "Chapter",
    "issue.field.estimate": "Estimate",
    "issue.field.dueDate": "Due date",
    "issue.field.product": "Product",
    "issue.field.client": "Client",
    "issue.field.project": "Project",
    "issue.field.researchTrack": "Research track",
    "issue.field.cycle": "Cycle",
    "issue.field.roadmapMilestone": "Roadmap milestone",
    "issue.field.parentIssue": "Parent issue",
    "issue.field.labels": "Labels",
    "issue.field.updated": "Updated",
    "issue.action.changeStatus": "Change status",
    "issue.action.edit": "Edit issue",
    "issue.action.delete": "Delete issue",
    "issue.action.confirmDelete": "Confirm delete",
    "issue.action.cancelDelete": "Keep issue",
    "issue.action.submitComment": "Submit comment",
    "issue.create.title": "Create issue",
    "issue.create.submit": "New issue",
    "issue.create.error": "Unable to create the issue.",
    "issue.edit.title": "Edit issue",
    "issue.edit.submit": "Save changes",
    "issue.edit.error": "Unable to save the issue.",
    "issue.inline.edit": "Edit",
    "issue.inline.saved": "Changes saved.",
    "issue.inline.titleRequired": "Enter an issue title.",
    "issue.inline.estimateInvalid": "Use a whole number of zero or more.",
    "issue.inline.dateInvalid": "Enter a valid date and time.",
    "issue.inline.optionsError": "Unable to load the available options.",
    "issue.inline.unavailable": "Not available",
    "issue.inline.fieldHint": "Enter to save · Esc to cancel",
    "issue.inline.descriptionHint": "Ctrl/Cmd + Enter to save · Esc to cancel",
    "issue.inline.dateHint": "Date and time in UTC · Empty to remove",
    "issue.delete.error": "Unable to delete the issue.",
    "issue.delete.forbidden": "You do not have permission to delete this issue.",
    "issue.labels.none": "No labels",
    "issue.comment.placeholder": "Add a comment",
    "issue.comment.error": "Unable to add comment.",
    "issue.owner.placeholder": "Search by name or email",
    "issue.owner.clear": "Clear owner",
    "issue.userAutocomplete.noResults": "No users match that search.",
    "issue.status.error": "Unable to change status.",
    "issue.owner.error": "Unable to assign owner.",
    "issue.agent.error": "Unable to assign agent.",
    "issue.filter.search": "Search title or key",
    "issue.filter.allStatuses": "All statuses",
    "issue.filter.allPriorities": "All priorities",
    "issue.filter.clearContext": "Clear context filter",
    "issue.filter.clear": "Clear filters",
    "issue.views.label": "Issue views",
    "issue.views.all": "All",
    "issue.views.mine": "Mine",
    "issue.views.open": "Open",
    "issue.views.blocked": "Blocked",
    "issue.views.agents": "With agent",
    "issue.sort.label": "Sort issues",
    "issue.sort.updatedDesc": "Recently updated",
    "issue.sort.updatedAsc": "Oldest updates",
    "issue.sort.priorityDesc": "Highest priority",
    "issue.sort.priorityAsc": "Lowest priority",
    "issue.owner.unknown": "Owner unavailable",
    "issue.filter.context": "Context filter applied",
    "issue.filter.active": "Filters applied",
    "issue.detail.properties": "Properties",
    "issue.detail.context": "Context",
    "issue.detail.created": "Created",
    "issue.detail.noDueDate": "No due date",
    "issue.detail.breadcrumb": "Issue navigation",
    "issue.detail.activityFilter": "Filter activity",
    "issue.detail.changes": "Changes",
    "issue.activity.created": "created the issue",
    "issue.activity.updated": "updated the issue",
    "issue.activity.status": "changed the status",
    "issue.activity.owner": "changed the owner",
    "issue.activity.agent": "changed the agent",
    "issue.activity.comment": "added a comment",
    "issue.activity.runStarted": "started an agent run",
    "issue.activity.runUpdated": "updated an agent run",
    "issue.activity.waiting": "requested human input",
    "issue.activity.runCompleted": "completed an agent run",
    "issue.activity.runFailed": "reported an agent run failure",
    "issue.activity.artifact": "attached an artifact",
    "issue.activity.pr": "linked a pull request",
    "context.selectRecord": "Select a record to inspect its details.",
    "issue.status.triage": "Triage",
    "issue.status.backlog": "Backlog",
    "issue.status.todo": "Todo",
    "issue.status.inProgress": "In progress",
    "issue.status.inReview": "In review",
    "issue.status.blocked": "Blocked",
    "issue.status.done": "Done",
    "issue.status.canceled": "Canceled",
    "issue.priority.none": "None",
    "issue.priority.low": "Low",
    "issue.priority.medium": "Medium",
    "issue.priority.high": "High",
    "issue.priority.urgent": "Urgent",
    "issue.type.task": "Task",
    "issue.type.bug": "Bug",
    "issue.type.feature": "Feature",
    "issue.type.research": "Research",
    "issue.type.spike": "Spike",
    "issue.type.clientRequest": "Client request",
    "issue.type.internalOps": "Internal ops",
    "issue.type.qa": "QA",
    "issue.type.devops": "DevOps",
    "issue.type.agentTask": "Agent task",
    "inbox.queue": "Notification queue",
    "inbox.unreadTab": "Unread",
    "inbox.readTab": "Read",
    "inbox.markAllRead": "Mark all as read",
    "inbox.markRead": "Mark as read",
    "inbox.markUnread": "Mark as unread",
    "inbox.empty": "Select a notification to inspect its issue.",
    "inbox.empty.title": "Nothing selected",
    "inbox.empty.desc":
      "Pick a notification from the queue to review its linked issue and activity.",
    "inbox.emptyUnread": "No unread notifications.",
    "inbox.emptyUnread.desc":
      "You are all caught up. New signals from agents and teammates will land here.",
    "inbox.emptyRead": "No read notifications yet.",
    "inbox.emptyRead.desc": "Notifications you have reviewed will be archived here.",
    "inbox.actionError": "Unable to update this notification.",
    "inbox.metric.unread": "Unread",
    "inbox.metric.agent": "Agent",
    "inbox.metric.esNotes": "ES notes",
    "inbox.preview.unread": "Unread",
    "inbox.preview.read": "Read",
    "inbox.preview.noType": "Notification",
    "inbox.preview.linkedIssue": "Linked issue",
    "inbox.signal.source": "Source",
    "inbox.signal.signal": "Signal",
    "inbox.signal.action": "Action",
    "inbox.signal.notification": "Notification",
    "inbox.signal.selectedItem": "Selected item",
    "inbox.signal.issue": "Issue",
    "inbox.signal.nextStep": "Next step",
    "inbox.signal.reviewContext": "Review context",
    "inbox.activity.title": "Selected notification",
    "triage.queue": "Triage queue",
    "triage.accept": "Accept",
    "triage.backlog": "Backlog",
    "triage.start": "Start",
    "triage.empty": "No triage items.",
    "triage.empty.desc": "Incoming work waiting for a decision will show up here.",
    "triage.agentProposal": "Agent proposal ready for decision.",
    "triage.actionError": "Unable to move this issue.",
    "context.meta": "Context",
    "context.metric.records": "Records",
    "context.metric.activeRecords": "With work",
    "context.metric.linkedIssues": "Linked issues",
    "context.metric.avgIssues": "Avg issues",
    "context.field.name": "Name",
    "context.field.status": "Status",
    "context.field.issues": "Issues",
    "context.field.focus": "Focus",
    "context.field.action": "Action",
    "context.action.openIssues": "Issues",
    "context.action.openFilteredList": "Open filtered issues",
    "context.action.newRecord": "New record",
    "context.action.editRecord": "Edit record",
    "context.action.deleteRecord": "Delete record",
    "context.action.confirmDelete": "Confirm delete",
    "context.action.cancelDelete": "Keep record",
    "context.form.createTitle": "Create record",
    "context.form.editTitle": "Edit record",
    "context.form.deleteHint":
      "Deletion is blocked automatically when the record is still referenced by active work.",
    "context.form.createError": "Unable to create the record.",
    "context.form.updateError": "Unable to update the record.",
    "context.form.deleteError": "Unable to delete the record.",
    "context.form.forbidden": "Only admins can change context records.",
    "context.field.key": "Key",
    "context.empty": "No context records match this view.",
    "context.value.operational": "Operational",
    "context.status.active": "Active",
    "context.status.completed": "Completed",
    "context.status.paused": "Paused",
    "context.status.archived": "Archived",
    "context.status.decided": "Decided",
    "context.status.exploring": "Exploring",
    "context.status.validating": "Validating",
    "products.title": "Products",
    "products.subtitle": "Internal Qualtio products with ownership and active work.",
    "products.list": "Product directory",
    "products.detail": "Product",
    "products.search": "Search products",
    "products.detailCopy":
      "Product records stay light and operational, with fast links back to execution.",
    "products.form.createTitle": "Create product",
    "products.form.editTitle": "Edit product",
    "clients.title": "Clients",
    "clients.subtitle": "Client accounts connected to delivery work without CRM overhead.",
    "clients.list": "Client directory",
    "clients.detail": "Client",
    "clients.search": "Search clients",
    "clients.detailCopy": "Client context stays lightweight and points back to active issues.",
    "clients.form.createTitle": "Create client",
    "clients.form.editTitle": "Edit client",
    "projects.title": "Projects / Engagements",
    "projects.subtitle": "Internal initiatives and client engagements with current work load.",
    "projects.list": "Project directory",
    "projects.detail": "Project",
    "projects.search": "Search projects",
    "projects.detailCopy": "Projects stay compact, explicit, and connected to active delivery.",
    "projects.form.createTitle": "Create project",
    "projects.form.editTitle": "Edit project",
    "research.title": "Research Tracks",
    "research.subtitle": "Hypotheses, findings, and decisions linked to execution work.",
    "research.list": "Research directory",
    "research.detail": "Track",
    "research.search": "Search research tracks",
    "research.detailCopy": "Research context links discovery and decisions to concrete work.",
    "research.form.createTitle": "Create research track",
    "research.form.editTitle": "Edit research track",
    "chapters.title": "Chapters",
    "chapters.subtitle": "Capability ownership and chapter workload across Qualtio.",
    "chapters.list": "Chapter directory",
    "chapters.detail": "Chapter",
    "chapters.search": "Search chapters",
    "chapters.detailCopy": "Chapter views show ownership and linked issue pressure.",
    "chapters.form.createTitle": "Create chapter",
    "chapters.form.editTitle": "Edit chapter",
    "planning.meta": "Planning",
    "planning.empty": "No planning records match this view.",
    "planning.openIssues": "Open issues",
    "planning.field.name": "Name",
    "planning.field.window": "Window",
    "planning.field.startsAt": "Starts",
    "planning.field.endsAt": "Ends",
    "planning.field.targetDate": "Target date",
    "planning.field.status": "Status",
    "planning.field.issues": "Issues",
    "planning.field.action": "Action",
    "planning.action.newRecord": "New record",
    "planning.action.editRecord": "Edit record",
    "planning.action.deleteRecord": "Delete record",
    "planning.action.confirmDelete": "Confirm delete",
    "planning.action.cancelDelete": "Keep record",
    "planning.form.deleteHint":
      "Deletion is blocked automatically when the record is still referenced by active work.",
    "planning.form.createError": "Unable to create the record.",
    "planning.form.updateError": "Unable to update the record.",
    "planning.form.deleteError": "Unable to delete the record.",
    "planning.form.forbidden": "Only admins can change planning records.",
    "planning.status.active": "Active",
    "planning.status.closed": "Closed",
    "planning.status.planned": "Planned",
    "planning.status.shipped": "Shipped",
    "cycles.title": "Cycles",
    "cycles.subtitle": "Lightweight cycle planning with issue counts and health.",
    "cycles.metric.planned": "Planned issues",
    "cycles.metric.completed": "Completed",
    "cycles.metric.carryOver": "Carry-over",
    "cycles.metric.capacity": "Estimated points",
    "cycles.completedShort": "done",
    "cycles.list": "Cycle overview",
    "cycles.health": "Current cycle health",
    "cycles.progress.completed": "Completed",
    "cycles.progress.blocked": "Blocked",
    "cycles.openPlan": "Open cycle plan",
    "cycles.form.createTitle": "Create cycle",
    "cycles.form.editTitle": "Edit cycle",
    "roadmap.title": "Roadmap",
    "roadmap.subtitle": "Milestone view for strategic product and delivery commitments.",
    "roadmap.noTarget": "No target",
    "roadmap.openMilestone": "Open milestone",
    "roadmap.form.createTitle": "Create milestone",
    "roadmap.form.editTitle": "Edit milestone",
    "analytics.meta": "Analytics",
    "analytics.title": "Analytics",
    "analytics.subtitle": "Flow, agent, and distribution metrics from seeded read models.",
    "analytics.metric.open": "Open issues",
    "analytics.metric.open.helper": "Current non-completed work in the workspace.",
    "analytics.metric.completed": "Completed issues",
    "analytics.metric.completed.helper": "Completed work in the seeded rollup period.",
    "analytics.metric.blocked": "Blocked issues",
    "analytics.metric.blocked.helper": "Work currently blocked and needing attention.",
    "analytics.metric.agent": "Agent completions",
    "analytics.metric.agent.helper": "Agent runs completed in the rollup.",
    "analytics.drillDown": "Drill down",
    "analytics.throughput": "Cycle throughput",
    "analytics.distribution": "Work distribution",
    "analytics.completedShort": "completed",
    "analytics.insufficientData": "No analytics data is available yet.",
    "analytics.field.axis": "Axis",
    "analytics.field.issues": "Issues",
    "analytics.field.load": "Load",
    "analytics.field.action": "Action",
    "agents.meta": "Agent execution",
    "agents.title": "Agents",
    "agents.subtitle": "Operational agent capacity, recent runs, and human handoff state.",
    "agents.count": "{count} agents",
    "agents.list": "Agent directory",
    "agents.recentRuns": "Recent runs",
    "agents.startRun": "Start run",
    "agents.selectIssue": "Select an assigned issue",
    "agents.goal": "Goal",
    "agents.selected": "Selected agent",
    "agents.noRuns": "No agent runs yet.",
    "agents.noRuns.desc": "Start a run from an assigned issue to see execution history here.",
    "agents.runStartError": "Unable to start agent run.",
    "agents.runUpdateError": "Unable to update this agent run.",
    "agents.artifactError": "Unable to attach this artifact.",
    "agents.runNotFound": "Agent run not found.",
    "agents.runMeta": "AgentRun",
    "agents.runStory": "Run story",
    "agents.status": "Status",
    "agents.notStarted": "Not started",
    "agents.noGoal": "No goal yet",
    "agents.source": "Source",
    "agents.branch": "Branch",
    "agents.pullRequest": "Pull request",
    "agents.started": "Started",
    "agents.completed": "Completed",
    "agents.actions": "Actions",
    "agents.message": "Summary, blocker, or error",
    "agents.markRunning": "Mark running",
    "agents.requestHuman": "Request human",
    "agents.complete": "Complete",
    "agents.fail": "Fail",
    "agents.attachArtifact": "Attach artifact",
    "agents.artifactTitle": "Artifact title",
    "agents.artifactBody": "Artifact note",
    "agents.defaultSummary": "Completed by agent.",
    "agents.defaultError": "Agent run failed.",
    "agents.defaultBlocker": "Human input requested.",
    "agents.metric.total": "Total runs",
    "agents.metric.active": "Active",
    "agents.metric.waiting": "Waiting",
    "agents.metric.completed": "Completed",
    "slack.meta": "Slack Socket Mode",
    "slack.title": "Slack Settings",
    "slack.subtitle": "Configure Slack entry points without storing workspace secrets in WorkHub.",
    "slack.configured": "Configured",
    "slack.notConfigured": "Needs setup",
    "slack.workspaceConfig": "Workspace behavior",
    "slack.enabled": "Enable Slack",
    "slack.defaultChannel": "Default channel ID",
    "slack.save": "Save settings",
    "slack.saved": "Slack settings saved.",
    "slack.savedNotOperational":
      "Settings saved, but Slack is not operational until secrets are configured.",
    "slack.invalidChannel": "Enter a Slack channel ID starting with C or G.",
    "slack.setup": "Setup checklist",
    "slack.stepCreateApp": "Create a Slack app with Socket Mode enabled for the workspace.",
    "slack.stepSocketMode": "Add slash commands and message actions, then install the app.",
    "slack.stepSecrets":
      "Provide SLACK_BOT_TOKEN, SLACK_APP_TOKEN, and SLACK_SIGNING_SECRET through environment or a secret manager.",
    "slack.socketMode": "Socket Mode",
    "slack.secrets": "Secrets",
    "slack.ready": "Ready",
    "slack.notReady": "Not ready",
    "slack.present": "Present",
    "slack.missing": "Missing",
    "slack.handlers": "Handlers and scopes",
    "slack.scope": "scope",
    "apiKeys.meta": "User access",
    "apiKeys.title": "API Keys",
    "apiKeys.subtitle": "Generate scoped user keys for CLI and local agent tooling.",
    "apiKeys.create": "Create API key",
    "apiKeys.name": "Key name",
    "apiKeys.generate": "Generate key",
    "apiKeys.created": "Copy this token now. WorkHub will not show it again.",
    "apiKeys.copyToken": "Copy token",
    "apiKeys.copied": "Copied",
    "apiKeys.usage": "CLI usage",
    "apiKeys.usageMcp":
      "Set WORKHUB_API_KEY to this token when running qwork or local automation against WorkHub.",
    "apiKeys.usageSecret":
      "Treat API keys like passwords. Store them in a local secret manager or environment variable.",
    "apiKeys.active": "Active keys",
    "apiKeys.empty": "No API keys have been created yet.",
    "apiKeys.cancelRevoke": "Cancel revoke",
    "apiKeys.confirmRevoke": "Confirm revoke",
    "apiKeys.revokeConfirm": "Revoke {name}? This cannot be undone.",
    "apiKeys.revoke": "Revoke",
    "users.activeAdmins": "Active admins",
    "users.create": "Pre-provision user",
    "users.directory": "User directory",
    "users.email": "Email",
    "users.emailPlaceholder": "new.member@qualt.io",
    "users.empty": "No users match this view.",
    "users.lastAdminGuard": "At least one active admin must remain.",
    "users.lastLogin": "Last login",
    "users.meta": "Admin",
    "users.name": "Name",
    "users.role": "Role",
    "users.role.admin": "Admin",
    "users.role.member": "Member",
    "users.saveError": "Unable to save user changes.",
    "users.search": "Search users",
    "users.status": "Status",
    "users.status.active": "Active",
    "users.status.disabled": "Disabled",
    "users.subtitle": "Pre-provision accounts and manage access lifecycle.",
    "users.title": "Users",
    "github.meta": "GitHub App",
    "github.title": "GitHub Settings",
    "github.subtitle": "Configure a GitHub App installation without storing secrets in WorkHub.",
    "github.configured": "Configured",
    "github.notConfigured": "Needs setup",
    "github.appConfig": "App metadata",
    "github.enabled": "Enable GitHub",
    "github.organization": "Organization",
    "github.appId": "App ID",
    "github.clientId": "Client ID",
    "github.installationId": "Installation ID",
    "github.appSlug": "App slug",
    "github.repositorySelection": "Repository access",
    "github.repoAll": "All repositories",
    "github.repoSelected": "Selected repositories",
    "github.save": "Save settings",
    "github.sync": "Sync repositories",
    "github.saved": "GitHub settings saved.",
    "github.savedNotOperational":
      "Settings saved, but GitHub is not operational until secrets are configured.",
    "github.notReadyToSync": "Sync is disabled until the GitHub App secrets are configured.",
    "github.synced": "GitHub repository sync completed.",
    "github.setup": "Setup checklist",
    "github.stepCreateApp":
      "Create a GitHub App for the organization and grant metadata, contents, and pull request read permissions.",
    "github.stepInstallAll":
      "Install the app from GitHub with access to All repositories when WorkHub should read every authorized repo.",
    "github.stepSecrets":
      "Provide private key, webhook secret, and optional client secret through environment or a secret manager.",
    "github.repositories": "Repositories",
    "github.secrets": "Secrets",
    "github.present": "Present",
    "github.missing": "Missing",
    "github.detectedRepos": "Detected repositories",
    "github.branchSuggestion": "Branch suggestion",
    "github.suggestBranch": "Suggest branch",
    "github.branchSuggestError": "Unable to suggest a branch.",
    "github.prUrl": "Pull request URL",
    "github.prUrlInvalid": "Enter a valid GitHub pull request URL ending in /pull/<number>.",
    "github.linkPr": "Link PR",
    "github.prLinkError": "Unable to link this pull request.",
  },
  es: {
    "nav.maintenance": "Mantenimiento",
    "maintenance.subtitle":
      "Consulta el estado del servicio, los procesos y los datos del espacio de trabajo.",
    "maintenance.statusTitle": "Servicio y datos",
    "maintenance.refresh": "Actualizar estado",
    "maintenance.connected": "Servicio y almacenamiento disponibles",
    "maintenance.checkFailed": "Comprobación fallida",
    "maintenance.lastCheck": "Última comprobación:",
    "maintenance.temporaryData": "Datos temporales de desarrollo",
    "maintenance.persistentData": "Almacenamiento persistente",
    "maintenance.queueTitle": "Procesamiento de eventos",
    "maintenance.pending": "Pendientes",
    "maintenance.processing": "En proceso",
    "maintenance.failed": "Fallidos",
    "maintenance.queueHint":
      "Eventos registrados pendientes de procesar, en curso o detenidos tras fallar. Consulta la fecha de comprobación al revisar estos contadores.",
    "maintenance.oldestPending": "Evento pendiente más antiguo:",
    "maintenance.statusError":
      "No se pudo comprobar el estado del servicio y los datos. Actualiza para reintentar.",
    "maintenance.dataTitle": "Limpieza de datos",
    "maintenance.protection":
      "Se conservan las cuentas reales, los registros modificados y los datos demo vinculados a trabajo real. También el espacio de trabajo, los estados y la configuración de integraciones.",
    "maintenance.preview": "Generar vista previa",
    "maintenance.demo": "Datos de demostración",
    "maintenance.start":
      "Genera una vista previa de solo lectura para ver cada borrado propuesto y los registros que se conservarán. Este paso no elimina nada.",
    "maintenance.review": "Revisar registros",
    "maintenance.expires": "La vista previa caduca a las",
    "maintenance.action": "Mostrar registros",
    "maintenance.delete": "Eliminar registros demo",
    "maintenance.keep": "Conservar registros",
    "maintenance.search": "Buscar por nombre o ID",
    "maintenance.noRecords": "No hay registros para este filtro.",
    "maintenance.record": "Registro",
    "maintenance.reason": "Motivo",
    "maintenance.reason.unchanged_demo": "Idéntico al dato demo original",
    "maintenance.reason.modified_demo": "El registro demo se ha modificado",
    "maintenance.reason.real_data": "No pertenece a los datos demo",
    "maintenance.reason.current_admin": "Administrador actual",
    "maintenance.reason.referenced": "Referenciado por un registro conservado",
    "maintenance.reason.mixed_issue": "La tarea contiene trabajo o historial conservado",
    "maintenance.confirmTitle": "Confirmar la limpieza revisada",
    "maintenance.irreversible":
      "El borrado es permanente. Esta página no crea una copia de seguridad. Si cambian los datos, se exigirá una nueva vista previa.",
    "maintenance.backup":
      "He verificado una copia de seguridad reciente y revisado cada borrado propuesto.",
    "maintenance.typeConfirmation": "Escribe DELETE DEMO para confirmar",
    "maintenance.loadError": "No se pudo generar la vista previa. Inténtalo de nuevo.",
    "maintenance.saveError":
      "No se pudo confirmar la limpieza. Genera una nueva vista previa para comprobar el estado actual antes de reintentar.",
    "maintenance.stale":
      "La vista previa ha caducado o han cambiado los datos. Genera y revisa una nueva.",
    "maintenance.done": "Limpieza completada. Registros eliminados:",
    "maintenance.nothingToDelete":
      "No hay registros demo sin modificar que se puedan eliminar de forma segura.",
    "maintenance.labels": "Etiquetas",
    "maintenance.issueLabels": "Etiquetas de tareas",
    "maintenance.comments": "Comentarios",
    "maintenance.activity": "Historial de actividad",
    "maintenance.views": "Vistas guardadas",
    "maintenance.runs": "Ejecuciones de agentes",
    "maintenance.artifacts": "Artefactos",
    "maintenance.repositories": "Repositorios vinculados",
    "maintenance.pullRequests": "Enlaces a pull requests",
    "maintenance.other": "Otros registros",
    "app.name": "Qualtio WorkHub",
    "app.workspace": "Qualtio",
    "app.loading": "Cargando",
    "auth.demoHint": "Usa demo@qualt.io en modo desarrollo.",
    "auth.email": "Email",
    "auth.error": "No se pudo iniciar sesión con esas credenciales.",
    "auth.googleHint": "Solo cuentas Google de Qualtio pueden entrar.",
    "auth.googleLogin": "Continuar con Google",
    "auth.loading": "Cargando opciones de inicio de sesión...",
    "auth.login": "Entrar",
    "auth.logout": "Salir",
    "auth.password": "Contraseña",
    "auth.subtitle": "Gestión de trabajo agent-first para Qualtio.",
    "auth.title": "Entrar a WorkHub",
    "command.open": "Abrir paleta de comandos",
    "command.createIssue": "Crear issue",
    "command.placeholder": "Buscar rutas, acciones e issues",
    "command.shortcut": "Ctrl/Cmd+K",
    "command.search": "Buscar",
    "command.title": "Paleta de comandos",
    "command.empty": "Ningún comando o issue coincide con la búsqueda.",
    "command.results.one": "1 resultado",
    "command.results.many": "resultados",
    "common.language": "Idioma",
    "common.cancel": "Cancelar",
    "common.close": "Cerrar",
    "common.create": "Crear",
    "common.delete": "Borrar",
    "common.edit": "Editar",
    "common.remove": "Eliminar",
    "common.save": "Guardar",
    "common.saving": "Guardando",
    "common.retry": "Reintentar",
    "common.theme": "Tema",
    "common.themeDark": "Oscuro",
    "common.themeLight": "Claro",
    "settings.adminOnly": "Solo admins pueden cambiar configuraciones de integraciones.",
    "settings.forbidden": "No tienes permisos para cambiar esta configuración.",
    "nav.agents": "Agentes",
    "nav.analytics": "Analítica",
    "nav.board": "Tablero",
    "nav.chapters": "Capítulos",
    "nav.clients": "Clientes",
    "nav.cycles": "Ciclos",
    "nav.github": "GitHub",
    "nav.apiKeys": "API Keys",
    "nav.group.work": "Trabajo",
    "nav.group.agents": "Agentes",
    "nav.group.context": "Contexto",
    "nav.group.planning": "Planificación",
    "nav.group.settings": "Configuración",
    "nav.myWork": "Mi trabajo",
    "nav.inbox": "Bandeja",
    "nav.issues": "Issues",
    "nav.products": "Productos",
    "nav.projects": "Proyectos",
    "nav.research": "Investigación",
    "nav.roadmap": "Roadmap",
    "nav.slack": "Slack",
    "nav.triage": "Triaje",
    "nav.users": "Usuarios",
    "search.global": "Buscar o saltar",
    "shell.account": "Cuenta",
    "shell.navigation": "Navegación",
    "shell.skipToContent": "Saltar al contenido principal",
    "work.meta": "Workflow core",
    "work.loadError": "No se pudo cargar esta vista del workspace.",
    "work.loadError.desc": "Comprueba la conexión y reintenta. Se conservarán los filtros.",
    "work.empty": "No hay issues para esta vista.",
    "work.empty.desc": "Crea un issue o ajusta los filtros para poblar esta vista.",
    "work.home.agentQueueEmpty": "Aún no hay agentes asignados a tu trabajo.",
    "work.home.title": "Inicio / Mi trabajo",
    "work.home.subtitle": "Trabajo priorizado por SLA, ownership y confianza del agente.",
    "work.home.priority": "Cola prioritaria",
    "work.home.agentQueue": "Cola de agentes",
    "work.inbox.title": "Bandeja",
    "work.inbox.subtitle": "Notificaciones unificadas de trabajo, agentes e integraciones.",
    "work.triage.title": "Triaje",
    "work.triage.subtitle": "Cola de decisión rápida para trabajo entrante.",
    "work.issues.title": "Lista de issues",
    "work.issues.subtitle": "Filtra, inspecciona y abre trabajo sin perder contexto.",
    "work.board.title": "Tablero",
    "work.board.subtitle": "Tablero operativo de estados para el trabajo actual.",
    "work.board.scope": "Tablero",
    "work.board.allBoards": "Todo el trabajo",
    "work.board.chapterBoard": "Capítulo",
    "work.board.initiativeBoard": "Iniciativa",
    "work.board.selectedBoard": "Tablero seleccionado",
    "work.metric.open": "Abiertos",
    "work.metric.triage": "Triaje",
    "work.metric.agent": "Agente asignado",
    "work.metric.mine": "Míos",
    "work.metric.urgent": "Urgentes",
    "work.metric.slaRisk": "Riesgo SLA",
    "work.metric.agentDrafts": "Borradores agente",
    "work.metric.blocked": "Bloqueados",
    "issue.open": "Abrir issue",
    "issue.unassigned": "Sin asignar",
    "issue.noDescription": "Sin descripción aún.",
    "issue.comments": "Comentarios",
    "issue.timeline": "Actividad",
    "issue.agentPanel": "Ejecución de agente",
    "issue.githubPanel": "GitHub",
    "issue.githubPlaceholder": "No hay pull requests enlazadas.",
    "issue.slackPanel": "Contexto Slack",
    "issue.slackPlaceholder":
      "Los hilos aparecerán aquí cuando un mensaje de Slack se convierta o enlace.",
    "issue.slackSkeleton": "Skeleton Slack listo",
    "issue.slackNoThread": "Sin hilo enlazado",
    "issue.artifacts": "Artefactos",
    "issue.artifacts.empty": "Aún no hay artefactos adjuntos.",
    "issue.detail.overview": "Overview",
    "issue.detail.events": "eventos",
    "issue.detail.runs": "runs",
    "issue.detail.noSla": "Sin SLA",
    "issue.detail.impact": "Impacto",
    "issue.detail.testsRunning": "tests ejecutándose",
    "issue.detail.currentState": "Estado actual",
    "issue.detail.action.start": "Empezar trabajo",
    "issue.detail.commentsMeta": "Slack + interno",
    "issue.detail.commentRole": "Interno",
    "issue.detail.noComments": "Aún no hay comentarios.",
    "issue.detail.liveActivity": "actividad en vivo",
    "issue.detail.noActivity": "Sin actividad todavía",
    "issue.detail.latestRun": "último run",
    "issue.detail.runningTests": "Progreso del run",
    "issue.detail.metadata": "Metadatos",
    "issue.detail.fields": "campos",
    "issue.detail.repo": "repo",
    "issue.detail.notFound": "Issue no encontrado.",
    "issue.field.issue": "Issue",
    "issue.field.title": "Título",
    "issue.field.description": "Descripción",
    "issue.field.status": "Estado",
    "issue.field.priority": "Prioridad",
    "issue.field.type": "Tipo",
    "issue.field.assigneeUser": "Responsable",
    "issue.field.agent": "Agente",
    "issue.field.chapter": "Capítulo",
    "issue.field.estimate": "Estimación",
    "issue.field.dueDate": "Fecha límite",
    "issue.field.product": "Producto",
    "issue.field.client": "Cliente",
    "issue.field.project": "Proyecto",
    "issue.field.researchTrack": "Línea de investigación",
    "issue.field.cycle": "Ciclo",
    "issue.field.roadmapMilestone": "Hito",
    "issue.field.parentIssue": "Issue padre",
    "issue.field.labels": "Etiquetas",
    "issue.field.updated": "Actualizado",
    "issue.action.changeStatus": "Cambiar estado",
    "issue.action.edit": "Editar issue",
    "issue.action.delete": "Borrar issue",
    "issue.action.confirmDelete": "Confirmar borrado",
    "issue.action.cancelDelete": "Mantener issue",
    "issue.action.submitComment": "Enviar comentario",
    "issue.create.title": "Crear issue",
    "issue.create.submit": "Nuevo issue",
    "issue.create.error": "No se pudo crear el issue.",
    "issue.edit.title": "Editar issue",
    "issue.edit.submit": "Guardar cambios",
    "issue.edit.error": "No se pudo guardar el issue.",
    "issue.inline.edit": "Editar",
    "issue.inline.saved": "Cambios guardados.",
    "issue.inline.titleRequired": "Escribe un título para el issue.",
    "issue.inline.estimateInvalid": "Usa un número entero igual o mayor que cero.",
    "issue.inline.dateInvalid": "Introduce una fecha y hora válidas.",
    "issue.inline.optionsError": "No se pudieron cargar las opciones disponibles.",
    "issue.inline.unavailable": "No disponible",
    "issue.inline.fieldHint": "Enter para guardar · Esc para cancelar",
    "issue.inline.descriptionHint": "Ctrl/Cmd + Enter para guardar · Esc para cancelar",
    "issue.inline.dateHint": "Fecha y hora en UTC · Vacío para quitarla",
    "issue.delete.error": "No se pudo borrar el issue.",
    "issue.delete.forbidden": "No tienes permisos para borrar este issue.",
    "issue.labels.none": "Sin labels",
    "issue.comment.placeholder": "Agregar comentario",
    "issue.comment.error": "No se pudo agregar el comentario.",
    "issue.owner.placeholder": "Buscar por nombre o email",
    "issue.owner.clear": "Quitar responsable",
    "issue.userAutocomplete.noResults": "Ningún usuario coincide con esa búsqueda.",
    "issue.status.error": "No se pudo cambiar el estado.",
    "issue.owner.error": "No se pudo asignar el responsable.",
    "issue.agent.error": "No se pudo asignar el agente.",
    "issue.filter.search": "Buscar título o clave",
    "issue.filter.allStatuses": "Todos los estados",
    "issue.filter.allPriorities": "Todas las prioridades",
    "issue.filter.clearContext": "Limpiar filtro de contexto",
    "issue.filter.clear": "Limpiar filtros",
    "issue.views.label": "Vistas de issues",
    "issue.views.all": "Todos",
    "issue.views.mine": "Míos",
    "issue.views.open": "Abiertos",
    "issue.views.blocked": "Bloqueados",
    "issue.views.agents": "Con agente",
    "issue.sort.label": "Ordenar issues",
    "issue.sort.updatedDesc": "Actualizados recientemente",
    "issue.sort.updatedAsc": "Actualizaciones más antiguas",
    "issue.sort.priorityDesc": "Mayor prioridad",
    "issue.sort.priorityAsc": "Menor prioridad",
    "issue.owner.unknown": "Responsable no disponible",
    "issue.filter.context": "Filtro de contexto aplicado",
    "issue.filter.active": "Filtros aplicados",
    "issue.detail.properties": "Propiedades",
    "issue.detail.context": "Contexto",
    "issue.detail.created": "Creado",
    "issue.detail.noDueDate": "Sin fecha límite",
    "issue.detail.breadcrumb": "Navegación del issue",
    "issue.detail.activityFilter": "Filtrar actividad",
    "issue.detail.changes": "Cambios",
    "issue.activity.created": "creó el issue",
    "issue.activity.updated": "actualizó el issue",
    "issue.activity.status": "cambió el estado",
    "issue.activity.owner": "cambió el responsable",
    "issue.activity.agent": "cambió el agente",
    "issue.activity.comment": "añadió un comentario",
    "issue.activity.runStarted": "inició un run de agente",
    "issue.activity.runUpdated": "actualizó un run de agente",
    "issue.activity.waiting": "solicitó intervención humana",
    "issue.activity.runCompleted": "completó un run de agente",
    "issue.activity.runFailed": "registró un fallo de agente",
    "issue.activity.artifact": "adjuntó un artefacto",
    "issue.activity.pr": "enlazó un pull request",
    "context.selectRecord": "Selecciona un registro para ver su detalle.",
    "issue.status.triage": "Triaje",
    "issue.status.backlog": "Backlog",
    "issue.status.todo": "Por hacer",
    "issue.status.inProgress": "En progreso",
    "issue.status.inReview": "En revisión",
    "issue.status.blocked": "Bloqueado",
    "issue.status.done": "Hecho",
    "issue.status.canceled": "Cancelado",
    "issue.priority.none": "Ninguna",
    "issue.priority.low": "Baja",
    "issue.priority.medium": "Media",
    "issue.priority.high": "Alta",
    "issue.priority.urgent": "Urgente",
    "issue.type.task": "Tarea",
    "issue.type.bug": "Bug",
    "issue.type.feature": "Feature",
    "issue.type.research": "Investigación",
    "issue.type.spike": "Spike",
    "issue.type.clientRequest": "Pedido de cliente",
    "issue.type.internalOps": "Ops interno",
    "issue.type.qa": "QA",
    "issue.type.devops": "DevOps",
    "issue.type.agentTask": "Tarea de agente",
    "inbox.queue": "Cola de notificaciones",
    "inbox.unreadTab": "No leídas",
    "inbox.readTab": "Leídas",
    "inbox.markAllRead": "Marcar todas como leídas",
    "inbox.markRead": "Marcar como leída",
    "inbox.markUnread": "Marcar como no leída",
    "inbox.empty": "Selecciona una notificación para inspeccionar su issue.",
    "inbox.empty.title": "Nada seleccionado",
    "inbox.empty.desc":
      "Elige una notificación de la cola para revisar su issue enlazado y su actividad.",
    "inbox.emptyUnread": "No hay notificaciones no leídas.",
    "inbox.emptyUnread.desc":
      "Estás al día. Las nuevas señales de agentes y del equipo aparecerán aquí.",
    "inbox.emptyRead": "Aún no hay notificaciones leídas.",
    "inbox.emptyRead.desc": "Las notificaciones que revises se archivarán aquí.",
    "inbox.actionError": "No se pudo actualizar esta notificación.",
    "inbox.metric.unread": "No leídas",
    "inbox.metric.agent": "Agente",
    "inbox.metric.esNotes": "Notas ES",
    "inbox.preview.unread": "No leída",
    "inbox.preview.read": "Leída",
    "inbox.preview.noType": "Notificación",
    "inbox.preview.linkedIssue": "Issue enlazado",
    "inbox.signal.source": "Origen",
    "inbox.signal.signal": "Señal",
    "inbox.signal.action": "Acción",
    "inbox.signal.notification": "Notificación",
    "inbox.signal.selectedItem": "Item seleccionado",
    "inbox.signal.issue": "Issue",
    "inbox.signal.nextStep": "Siguiente paso",
    "inbox.signal.reviewContext": "Revisar contexto",
    "inbox.activity.title": "Notificación seleccionada",
    "triage.queue": "Cola de triaje",
    "triage.accept": "Aceptar",
    "triage.backlog": "Backlog",
    "triage.start": "Empezar",
    "triage.empty": "No hay items de triaje.",
    "triage.empty.desc": "El trabajo entrante pendiente de decisión aparecerá aquí.",
    "triage.agentProposal": "Propuesta de agente lista para decisión.",
    "triage.actionError": "No se pudo mover este issue.",
    "context.meta": "Contexto",
    "context.metric.records": "Registros",
    "context.metric.activeRecords": "Con trabajo",
    "context.metric.linkedIssues": "Issues enlazados",
    "context.metric.avgIssues": "Media issues",
    "context.field.name": "Nombre",
    "context.field.status": "Estado",
    "context.field.issues": "Issues",
    "context.field.focus": "Foco",
    "context.field.action": "Acción",
    "context.action.openIssues": "Issues",
    "context.action.openFilteredList": "Abrir issues filtrados",
    "context.action.newRecord": "Nuevo registro",
    "context.action.editRecord": "Editar registro",
    "context.action.deleteRecord": "Borrar registro",
    "context.action.confirmDelete": "Confirmar borrado",
    "context.action.cancelDelete": "Mantener registro",
    "context.form.createTitle": "Crear registro",
    "context.form.editTitle": "Editar registro",
    "context.form.deleteHint":
      "El borrado se bloquea automáticamente cuando el registro sigue referenciado por trabajo activo.",
    "context.form.createError": "No se pudo crear el registro.",
    "context.form.updateError": "No se pudo actualizar el registro.",
    "context.form.deleteError": "No se pudo borrar el registro.",
    "context.form.forbidden": "Solo admins pueden cambiar registros de contexto.",
    "context.field.key": "Clave",
    "context.empty": "No hay registros de contexto para esta vista.",
    "context.value.operational": "Operativo",
    "context.status.active": "Activo",
    "context.status.completed": "Completado",
    "context.status.paused": "Pausado",
    "context.status.archived": "Archivado",
    "context.status.decided": "Decidido",
    "context.status.exploring": "Explorando",
    "context.status.validating": "Validando",
    "products.title": "Productos",
    "products.subtitle": "Productos internos de Qualtio con ownership y trabajo activo.",
    "products.list": "Directorio de productos",
    "products.detail": "Producto",
    "products.search": "Buscar productos",
    "products.detailCopy":
      "Los productos se mantienen ligeros y operativos, con vuelta rápida a la ejecución.",
    "products.form.createTitle": "Crear producto",
    "products.form.editTitle": "Editar producto",
    "clients.title": "Clientes",
    "clients.subtitle": "Cuentas cliente conectadas al delivery sin peso de CRM.",
    "clients.list": "Directorio de clientes",
    "clients.detail": "Cliente",
    "clients.search": "Buscar clientes",
    "clients.detailCopy": "El contexto cliente se mantiene ligero y vuelve a issues activos.",
    "clients.form.createTitle": "Crear cliente",
    "clients.form.editTitle": "Editar cliente",
    "projects.title": "Proyectos / Engagements",
    "projects.subtitle": "Iniciativas internas y engagements cliente con carga actual.",
    "projects.list": "Directorio de proyectos",
    "projects.detail": "Proyecto",
    "projects.search": "Buscar proyectos",
    "projects.detailCopy":
      "Los proyectos se mantienen compactos, explícitos y conectados al delivery activo.",
    "projects.form.createTitle": "Crear proyecto",
    "projects.form.editTitle": "Editar proyecto",
    "research.title": "Research Tracks",
    "research.subtitle": "Hipótesis, hallazgos y decisiones enlazadas a ejecución.",
    "research.list": "Directorio de investigación",
    "research.detail": "Track",
    "research.search": "Buscar tracks de investigación",
    "research.detailCopy":
      "El contexto de investigación conecta descubrimiento y decisiones con trabajo concreto.",
    "research.form.createTitle": "Crear research track",
    "research.form.editTitle": "Editar research track",
    "chapters.title": "Capítulos",
    "chapters.subtitle": "Ownership de capacidades y carga de capítulos en Qualtio.",
    "chapters.list": "Directorio de capítulos",
    "chapters.detail": "Capítulo",
    "chapters.search": "Buscar capítulos",
    "chapters.detailCopy":
      "Las vistas de capítulo muestran ownership y presión de issues enlazados.",
    "chapters.form.createTitle": "Crear capítulo",
    "chapters.form.editTitle": "Editar capítulo",
    "planning.meta": "Planificación",
    "planning.empty": "No hay registros de planificación para esta vista.",
    "planning.openIssues": "Abrir issues",
    "planning.field.name": "Nombre",
    "planning.field.window": "Ventana",
    "planning.field.startsAt": "Inicio",
    "planning.field.endsAt": "Fin",
    "planning.field.targetDate": "Fecha objetivo",
    "planning.field.status": "Estado",
    "planning.field.issues": "Issues",
    "planning.field.action": "Acción",
    "planning.action.newRecord": "Nuevo registro",
    "planning.action.editRecord": "Editar registro",
    "planning.action.deleteRecord": "Borrar registro",
    "planning.action.confirmDelete": "Confirmar borrado",
    "planning.action.cancelDelete": "Mantener registro",
    "planning.form.deleteHint":
      "El borrado se bloquea automáticamente cuando el registro sigue referenciado por trabajo activo.",
    "planning.form.createError": "No se pudo crear el registro.",
    "planning.form.updateError": "No se pudo actualizar el registro.",
    "planning.form.deleteError": "No se pudo borrar el registro.",
    "planning.form.forbidden": "Solo admins pueden cambiar registros de planificación.",
    "planning.status.active": "Activo",
    "planning.status.closed": "Cerrado",
    "planning.status.planned": "Planificado",
    "planning.status.shipped": "Enviado",
    "cycles.title": "Ciclos",
    "cycles.subtitle": "Planificación ligera de ciclos con conteos y salud.",
    "cycles.metric.planned": "Issues planificados",
    "cycles.metric.completed": "Completados",
    "cycles.metric.carryOver": "Arrastre",
    "cycles.metric.capacity": "Puntos estimados",
    "cycles.completedShort": "done",
    "cycles.list": "Resumen de ciclos",
    "cycles.health": "Salud del ciclo actual",
    "cycles.progress.completed": "Completados",
    "cycles.progress.blocked": "Bloqueados",
    "cycles.openPlan": "Abrir plan del ciclo",
    "cycles.form.createTitle": "Crear ciclo",
    "cycles.form.editTitle": "Editar ciclo",
    "roadmap.title": "Roadmap",
    "roadmap.subtitle": "Vista de milestones para compromisos de producto y delivery.",
    "roadmap.noTarget": "Sin fecha",
    "roadmap.openMilestone": "Abrir milestone",
    "roadmap.form.createTitle": "Crear milestone",
    "roadmap.form.editTitle": "Editar milestone",
    "analytics.meta": "Analítica",
    "analytics.title": "Analítica",
    "analytics.subtitle": "Métricas de flujo, agentes y distribución desde read models seed.",
    "analytics.metric.open": "Issues abiertos",
    "analytics.metric.open.helper": "Trabajo actual no completado en el workspace.",
    "analytics.metric.completed": "Issues completados",
    "analytics.metric.completed.helper": "Trabajo completado en el periodo del rollup.",
    "analytics.metric.blocked": "Issues bloqueados",
    "analytics.metric.blocked.helper": "Trabajo bloqueado que requiere atención.",
    "analytics.metric.agent": "Completados por agentes",
    "analytics.metric.agent.helper": "Agent runs completados en el rollup.",
    "analytics.drillDown": "Ver detalle",
    "analytics.throughput": "Throughput por ciclo",
    "analytics.distribution": "Distribución de trabajo",
    "analytics.completedShort": "completados",
    "analytics.insufficientData": "Aún no hay datos de analítica.",
    "analytics.field.axis": "Eje",
    "analytics.field.issues": "Issues",
    "analytics.field.load": "Carga",
    "analytics.field.action": "Acción",
    "agents.meta": "Ejecución de agentes",
    "agents.title": "Agentes",
    "agents.subtitle": "Capacidad operativa de agentes, runs recientes y handoff humano.",
    "agents.count": "{count} agentes",
    "agents.list": "Directorio de agentes",
    "agents.recentRuns": "Runs recientes",
    "agents.startRun": "Iniciar run",
    "agents.selectIssue": "Selecciona un issue asignado",
    "agents.goal": "Objetivo",
    "agents.selected": "Agente seleccionado",
    "agents.noRuns": "Aún no hay agent runs.",
    "agents.noRuns.desc":
      "Inicia un run desde un issue asignado para ver el historial de ejecución aquí.",
    "agents.runStartError": "No se pudo iniciar el agent run.",
    "agents.runUpdateError": "No se pudo actualizar este agent run.",
    "agents.artifactError": "No se pudo adjuntar este artefacto.",
    "agents.runNotFound": "Agent run no encontrado.",
    "agents.runMeta": "AgentRun",
    "agents.runStory": "Historia del run",
    "agents.status": "Estado",
    "agents.notStarted": "Sin iniciar",
    "agents.noGoal": "Sin objetivo aún",
    "agents.source": "Origen",
    "agents.branch": "Branch",
    "agents.pullRequest": "Pull request",
    "agents.started": "Inicio",
    "agents.completed": "Completado",
    "agents.actions": "Acciones",
    "agents.message": "Resumen, bloqueo o error",
    "agents.markRunning": "Marcar running",
    "agents.requestHuman": "Pedir humano",
    "agents.complete": "Completar",
    "agents.fail": "Fallar",
    "agents.attachArtifact": "Adjuntar artefacto",
    "agents.artifactTitle": "Título del artefacto",
    "agents.artifactBody": "Nota del artefacto",
    "agents.defaultSummary": "Completado por el agente.",
    "agents.defaultError": "El agent run falló.",
    "agents.defaultBlocker": "Se requiere input humano.",
    "agents.metric.total": "Runs totales",
    "agents.metric.active": "Activos",
    "agents.metric.waiting": "Esperando",
    "agents.metric.completed": "Completados",
    "slack.meta": "Slack Socket Mode",
    "slack.title": "Configuración Slack",
    "slack.subtitle": "Configura entradas de Slack sin guardar secretos del workspace en WorkHub.",
    "slack.configured": "Configurado",
    "slack.notConfigured": "Falta configurar",
    "slack.workspaceConfig": "Comportamiento del workspace",
    "slack.enabled": "Activar Slack",
    "slack.defaultChannel": "ID del canal por defecto",
    "slack.save": "Guardar configuración",
    "slack.saved": "Configuración Slack guardada.",
    "slack.savedNotOperational":
      "Configuración guardada, pero Slack no estará operativo hasta configurar los secretos.",
    "slack.invalidChannel": "Introduce un ID de canal de Slack que empiece por C o G.",
    "slack.setup": "Checklist de setup",
    "slack.stepCreateApp": "Crea una Slack app con Socket Mode activado para el workspace.",
    "slack.stepSocketMode": "Agrega slash commands y message actions, luego instala la app.",
    "slack.stepSecrets":
      "Entrega SLACK_BOT_TOKEN, SLACK_APP_TOKEN y SLACK_SIGNING_SECRET vía environment o secret manager.",
    "slack.socketMode": "Socket Mode",
    "slack.secrets": "Secretos",
    "slack.ready": "Listo",
    "slack.notReady": "No listo",
    "slack.present": "Presentes",
    "slack.missing": "Faltan",
    "slack.handlers": "Handlers y scopes",
    "slack.scope": "scope",
    "apiKeys.meta": "Acceso de usuario",
    "apiKeys.title": "API Keys",
    "apiKeys.subtitle": "Genera claves de usuario con scope para CLI y tooling local de agentes.",
    "apiKeys.create": "Crear API key",
    "apiKeys.name": "Nombre de la clave",
    "apiKeys.generate": "Generar clave",
    "apiKeys.created": "Copia este token ahora. WorkHub no lo volverá a mostrar.",
    "apiKeys.copyToken": "Copiar token",
    "apiKeys.copied": "Copiado",
    "apiKeys.usage": "Uso CLI",
    "apiKeys.usageMcp":
      "Define WORKHUB_API_KEY con este token al ejecutar qwork o automatización local contra WorkHub.",
    "apiKeys.usageSecret":
      "Trata las API keys como passwords. Guárdalas en un secret manager local o variable de entorno.",
    "apiKeys.active": "Claves activas",
    "apiKeys.empty": "Aún no se han creado API keys.",
    "apiKeys.cancelRevoke": "Cancelar revocación",
    "apiKeys.confirmRevoke": "Confirmar revocación",
    "apiKeys.revokeConfirm": "¿Revocar {name}? Esta acción no se puede deshacer.",
    "apiKeys.revoke": "Revocar",
    "users.activeAdmins": "Admins activos",
    "users.create": "Pre-provisionar usuario",
    "users.directory": "Directorio de usuarios",
    "users.email": "Email",
    "users.emailPlaceholder": "new.member@qualt.io",
    "users.empty": "No hay usuarios para esta vista.",
    "users.lastAdminGuard": "Debe quedar al menos un admin activo.",
    "users.lastLogin": "Último login",
    "users.meta": "Admin",
    "users.name": "Nombre",
    "users.role": "Rol",
    "users.role.admin": "Admin",
    "users.role.member": "Member",
    "users.saveError": "No se pudieron guardar los cambios del usuario.",
    "users.search": "Buscar usuarios",
    "users.status": "Estado",
    "users.status.active": "Activo",
    "users.status.disabled": "Desactivado",
    "users.subtitle": "Pre-provisiona cuentas y gestiona el lifecycle de acceso.",
    "users.title": "Usuarios",
    "github.meta": "GitHub App",
    "github.title": "Configuración GitHub",
    "github.subtitle": "Configura una GitHub App sin guardar secretos en WorkHub.",
    "github.configured": "Configurado",
    "github.notConfigured": "Falta configurar",
    "github.appConfig": "Metadata de app",
    "github.enabled": "Activar GitHub",
    "github.organization": "Organización",
    "github.appId": "App ID",
    "github.clientId": "Client ID",
    "github.installationId": "Installation ID",
    "github.appSlug": "App slug",
    "github.repositorySelection": "Acceso a repositorios",
    "github.repoAll": "Todos los repositorios",
    "github.repoSelected": "Repositorios seleccionados",
    "github.save": "Guardar configuración",
    "github.sync": "Sincronizar repos",
    "github.saved": "Configuración GitHub guardada.",
    "github.savedNotOperational":
      "Configuración guardada, pero GitHub no estará operativo hasta configurar los secretos.",
    "github.notReadyToSync":
      "La sincronización está desactivada hasta configurar los secretos de la GitHub App.",
    "github.synced": "Sincronización de repositorios GitHub completada.",
    "github.setup": "Checklist de setup",
    "github.stepCreateApp":
      "Crea una GitHub App para la organización con permisos de metadata, contents y pull requests en read.",
    "github.stepInstallAll":
      "Instala la app desde GitHub con acceso a All repositories cuando WorkHub deba leer todos los repos autorizados.",
    "github.stepSecrets":
      "Entrega private key, webhook secret y client secret opcional vía environment o secret manager.",
    "github.repositories": "Repositorios",
    "github.secrets": "Secretos",
    "github.present": "Presentes",
    "github.missing": "Faltan",
    "github.detectedRepos": "Repos detectados",
    "github.branchSuggestion": "Sugerencia de branch",
    "github.suggestBranch": "Sugerir branch",
    "github.branchSuggestError": "No se pudo sugerir una branch.",
    "github.prUrl": "URL del pull request",
    "github.prUrlInvalid":
      "Introduce una URL válida de pull request de GitHub terminada en /pull/<número>.",
    "github.linkPr": "Enlazar PR",
    "github.prLinkError": "No se pudo enlazar este pull request.",
  },
} as const;

export type TranslationKey = keyof (typeof dictionaries)["en"];

export function translate(locale: Locale, key: TranslationKey): string {
  return dictionaries[locale][key];
}
